import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useSelector } from "react-redux";
import Header from "../../../layouts/header";
import field from "../../../assets/images/2025-Program/golf-ball-flag.jpg";
import arrow from "../../../assets/icons/arrow-up.webp";
import "./globalRankings.css";

export default function GlobalRankings() {
  const { t, i18n } = useTranslation();
  const baseUrl = useSelector((state) => state.gtm.baseUrl);
  const language = localStorage.getItem("i18nextLng");
  const [rankings, setRankings] = useState([]);
  const [filteredRankings, setFilteredRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const fetchGlobalRankings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${baseUrl}/global-rankings`);

        if (response.data.success && response.data.data) {
          const data = response.data.data;
          setRankings(data);
          setFilteredRankings(data);

          // Extract unique countries for filter
          const uniqueCountries = [
            ...new Set(data.map((ranking) => ranking.country)),
          ].sort();
          setCountries(uniqueCountries);
        } else {
          setError(t("global_rankings.error_fetch") || "Failed to load rankings");
        }
      } catch (err) {
        console.error("Error fetching global rankings:", err);
        setError(t("global_rankings.error_fetch") || "Failed to load rankings. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchGlobalRankings();
  }, [baseUrl, t]);

  useEffect(() => {
    if (selectedCountry) {
      const filtered = rankings.filter(
        (ranking) => ranking.country === selectedCountry
      );
      setFilteredRankings(filtered);
    } else {
      setFilteredRankings(rankings);
    }
  }, [selectedCountry, rankings]);

  const getFlagUrl = (ranking) => {
    // RECOMMENDED: Use computed_flag_url from API (handles all cases automatically including Scotland, Wales, England)
    if (ranking.computed_flag_url) {
      return ranking.computed_flag_url;
    }
    
    // Fallback logic (if computed_flag_url is not available)
    // Handle regional flags (Scotland, Wales, England)
    if (ranking.country === 'Scotland') {
      return 'https://flagcdn.com/w40/gb-sct.png';
    }
    if (ranking.country === 'Wales') {
      return 'https://flagcdn.com/w40/gb-wls.png';
    }
    if (ranking.country === 'England') {
      return 'https://flagcdn.com/w40/gb-eng.png';
    }
    
    // Use stored flag URL or generate from country code
    if (ranking.flag_url_small) {
      return ranking.flag_url_small;
    }
    if (ranking.flag_url) {
      return ranking.flag_url;
    }
    if (ranking.country_code) {
      return `https://flagcdn.com/w40/${ranking.country_code.toLowerCase()}.png`;
    }
    
    return null; // No flag available
  };

  const getStatusIcon = (status) => {
    if (!status) return "";
    if (status.startsWith("up")) return "▲";
    if (status.startsWith("down")) return "▼";
    if (status === "unchanged") return "↔";
    return "";
  };

  const getLatestUpdate = () => {
    if (filteredRankings.length === 0) return null;
    return filteredRankings.reduce((latest, current) => {
      return new Date(current.updated_at) > new Date(latest.updated_at)
        ? current
        : latest;
    }, filteredRankings[0]);
  };

  const latestUpdate = getLatestUpdate();

  if (loading) {
    return (
      <div>
        <Header image={field} title={t("global_rankings.title") || "Global Golf Resorts Ranking"} />
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="w-9 h-9 border-4 border-black border-l-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header image={field} title={t("global_rankings.title") || "Global Golf Resorts Ranking"} />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-14">
      <Header image={field} title={t("global_rankings.title") || "Global Golf Resorts Ranking"} />
      
      {/* Content Section */}
      <div className="flex flex-col justify-center items-center">
        {filteredRankings.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">
              {t("global_rankings.no_rankings") || "No rankings found."}
            </p>
          </div>
        ) : (
          <div className="lg:flex lg:flex-col lg:justify-center lg:items-center overflow-x-auto w-full px-4">
            {/* Filter Section */}
            <div className="w-full flex justify-end items-center mt-4 mb-6">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00573f] text-sm md:text-base"
              >
                <option value="">
                  {t("global_rankings.all_countries") || "All Countries"}
                </option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            {/* Title and Last Updated */}
            <div className="text-center mb-12">
              <div className="text-3xl md:text-4xl text-center custom-font m-8">
                {t("global_rankings.title") || "Global Golf Resorts Ranking"}
              </div>
              {latestUpdate && (
                <div className="text-base md:text-lg text-gray-400">
                  <div>
                    <h1>
                      {language === "fr" ? `Dernière mise à jour: ` : `Last Updated: `}
                      {new Date(latestUpdate.updated_at).toLocaleString(
                        language === "fr" ? "fr-FR" : "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </h1>
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            <table className="table-auto w-full md:w-[80vw] border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-100 text-gray-600 font-semibold text-sm md:text-base text-start">
                  <th className="header_style py-3 px-4">
                    {t("ranking_content.table.ranking") || "Ranking"}
                  </th>
                  <th className="header_style py-3 px-4">
                    {t("global_rankings.table.resort") || "Resort"}
                  </th>
                  <th className="header_style py-3 px-4">
                    {t("global_rankings.table.country") || "Country"}
                  </th>
                  <th className="header_style py-3 px-4">
                    {t("global_rankings.table.status") || "Status"}
                  </th>
                </tr>
              </thead>
              <tbody className="font-semibold text-lg md:text-xl">
                {filteredRankings.map((ranking) => {
                  const flagUrl = getFlagUrl(ranking);
                  return (
                    <tr key={ranking.id} className="border-b border-gray-200">
                      <td className="text-center content_sty py-2 px-4">
                        <div className="flex gap-2 flex-row items-center justify-center">
                          {ranking.position}
                          <img src={arrow} width={8} alt="Arrow Icon" />
                        </div>
                      </td>
                      <td className="py-2 px-4">
                        <div className="flex items-center gap-2">
                          {flagUrl && (
                            <img
                              src={flagUrl}
                              alt={ranking.country}
                              className="w-8 h-6 object-cover rounded"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <span
                            className="whitespace-nowrap"
                            style={{
                              color: "rgb(22 163 74 / var(--tw-text-opacity, 1))",
                            }}
                          >
                            {ranking.resort_name}
                          </span>
                        </div>
                      </td>
                      <td className="text-start content_sty py-2 px-4">
                        {ranking.country}
                      </td>
                      <td className="text-center content_sty py-2 px-4">
                        {ranking.status ? (
                          <span className="text-xs md:text-sm">
                            {getStatusIcon(ranking.status)} {ranking.status}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

